import { z } from 'zod';

export const MerchantTimeZoneSchema: z.ZodType<string> = z.string().trim().min(1);

export type MerchantTimeZone = z.infer<typeof MerchantTimeZoneSchema>;

type DateFormatterPartKey =
    | 'day'
    | 'dayPeriod'
    | 'hour'
    | 'minute'
    | 'month'
    | 'second'
    | 'timeZoneName'
    | 'year';

const buildDateFormatterParts = (
    value: string,
    timeZone: MerchantTimeZone,
    options: Intl.DateTimeFormatOptions
): Readonly<Record<DateFormatterPartKey, string>> | null => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }

    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        ...options,
    });
    const parts = formatter.formatToParts(date);
    const partMap: Partial<Record<DateFormatterPartKey, string>> = {};

    for (const part of parts) {
        switch (part.type) {
            case 'day':
            case 'dayPeriod':
            case 'hour':
            case 'minute':
            case 'month':
            case 'second':
            case 'timeZoneName':
            case 'year':
                partMap[part.type] = part.value;
                break;
            default:
                break;
        }
    }

    return {
        day: partMap.day ?? '',
        dayPeriod: partMap.dayPeriod ?? '',
        hour: partMap.hour ?? '',
        minute: partMap.minute ?? '',
        month: partMap.month ?? '',
        second: partMap.second ?? '',
        timeZoneName: partMap.timeZoneName ?? '',
        year: partMap.year ?? '',
    };
};

export const formatTimestampForMerchantUi = (
    value: string,
    timeZone: MerchantTimeZone
): string => {
    const parts = buildDateFormatterParts(value, timeZone, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZoneName: 'short',
    });
    if (parts === null) {
        return value;
    }
    return `${parts.month} ${parts.day}, ${parts.year}, ${parts.hour}:${parts.minute} ${parts.dayPeriod} ${parts.timeZoneName}`.trim();
};

export const formatTimestampForMerchantReceipt = (
    value: string,
    timeZone: MerchantTimeZone
): string => {
    const parts = buildDateFormatterParts(value, timeZone, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZoneName: 'short',
    });
    if (parts === null) {
        return value;
    }
    return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second} ${parts.timeZoneName}`.trim();
};
